import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Bookmark, ChevronRight, FileText, User, UserCog, type LucideIcon } from 'lucide-react';
import { useIsAdmin } from '@/api/hooks';
import { ADMIN_SECTIONS } from '@/lib/navigation';
import { ROUTES } from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * Settings — opened from the ⚙ in the header (decided 2026-10-14: Admin left the main sidebar).
 * One frame for both `/account/*` (personal, everyone) and `/admin/*` (admins only, grouped by
 * module from `ADMIN_SECTIONS`). The URLs didn't change; only the navigation moved here.
 *
 * Every section collapses (closed by default, to keep the list short). The section holding the
 * current page opens automatically; sections opened by hand stay open (`cosmos:settings:open`).
 */

export const PERSONAL_SETTINGS: { to: string; label: string; icon: LucideIcon }[] = [
  { to: ROUTES.account.info, label: 'Account info', icon: User },
  { to: ROUTES.account.noteTemplates, label: 'Note templates', icon: FileText },
  { to: ROUTES.account.bookmarks, label: 'Bookmarks', icon: Bookmark },
];

const OPEN_KEY = 'cosmos:settings:open';
const SECTIONS = (isAdmin: boolean) => [
  { title: 'Personal', icon: UserCog, items: PERSONAL_SETTINGS },
  ...(isAdmin ? ADMIN_SECTIONS : []),
];

function Item({ to, label, icon: Icon }: { to: string; label: string; icon: LucideIcon }) {
  return (
    <NavLink to={to} end
      className={({ isActive }) => cn('flex items-center gap-2.5 rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors',
        isActive ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground')}>
      <Icon className="h-3.5 w-3.5 shrink-0" />{label}
    </NavLink>
  );
}

export function SettingsLayout() {
  const { data: isAdmin } = useIsAdmin();
  const { pathname } = useLocation();
  const sections = SECTIONS(!!isAdmin);
  const current = sections.find((s) => s.items.some((i) => i.to === pathname))?.title ?? null;

  const [open, setOpen] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(OPEN_KEY) ?? '[]') as string[]); } catch { return new Set(); }
  });
  useEffect(() => { try { localStorage.setItem(OPEN_KEY, JSON.stringify([...open])); } catch { /* storage unavailable */ } }, [open]);
  const toggle = (t: string) => setOpen((prev) => { const n = new Set(prev); if (n.has(t)) n.delete(t); else n.add(t); return n; });
  const allOpen = sections.every((s) => open.has(s.title));

  return (
    <div className="mx-auto w-full max-w-[1300px] px-4 py-6 md:px-8">
      <h1 className="mb-5 text-[18px] font-[650] tracking-[-0.02em] text-foreground">Settings</h1>
      <div className="flex flex-col gap-6 md:flex-row md:gap-8">
        <aside className="shrink-0 md:w-56">
          <nav aria-label="Settings" className="space-y-0.5">
            {sections.map((s) => {
              // The current page's section is always shown open (it can't be collapsed away from under you).
              const isOpen = open.has(s.title) || s.title === current;
              return (
                <div key={s.title}>
                  <button type="button" onClick={() => toggle(s.title)} aria-expanded={isOpen}
                    className={cn('flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] font-semibold transition-colors',
                      s.title === current ? 'text-foreground' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground')}>
                    <s.icon className="h-3.5 w-3.5 shrink-0" />
                    <span className="flex-1 truncate">{s.title}</span>
                    <ChevronRight className={cn('h-3.5 w-3.5 shrink-0 transition-transform', isOpen && 'rotate-90')} />
                  </button>
                  {isOpen && <div className="mb-1.5 ml-3 space-y-0.5 border-l border-divider pl-2">{s.items.map((i) => <Item key={i.to} to={i.to} label={i.label} icon={i.icon} />)}</div>}
                </div>
              );
            })}
            {sections.length > 2 && (
              <button type="button" onClick={() => setOpen(allOpen ? new Set() : new Set(sections.map((s) => s.title)))}
                className="mt-2 px-2 text-[11px] text-muted-foreground hover:text-foreground">{allOpen ? 'Collapse all' : 'Expand all'}</button>
            )}
          </nav>
        </aside>
        <div className="min-w-0 flex-1"><Outlet /></div>
      </div>
    </div>
  );
}
